import { ColumnDef } from '@tanstack/react-table';
import { Tooltip } from '@mui/material';
import { Button } from '../../../components/ui/button';
import { PermissionGuard } from '../../../components/common/PermissionGuard';
import { Badge } from '../../../components/ui/badge';
import { routePaths } from '../../../global/routePath';
import { useNavigate } from 'react-router-dom';

interface AlertColumnsProps {
    canEdit: boolean;
    canDelete: boolean;
    onDelete: (alert: any) => void;
    onSendAlert: (alert: any) => void;
    onEdit: (alert: any) => void;
}

export const getAlertColumns = ({
    canEdit,
    onDelete,
    onSendAlert,
    onEdit,
    canDelete
}: AlertColumnsProps): ColumnDef<Alert.AlertDto>[] => {
    const navigate = useNavigate();
    return [
        {
            accessorKey: 'title',
            header: 'Title',
            cell: ({ row }) => (
                <div className="font-medium max-w-[200px] truncate">
                    {row.original.title}
                </div>
            ),
        },
        {
            accessorKey: 'institutionName',
            header: 'Institution',
            cell: ({ row }) => (
                <div className="text-sm">
                    {row.original.institutionName}
                </div>
            ),
        },
        {
            accessorKey: 'alertType',
            header: 'Alert Type',
            cell: ({ row }) => {
                const getAlertTypeColor = (type: Alert.AlertType) => {
                    switch (type) {
                        case 'Emergency':
                            return 'bg-red-100 text-red-800 border-red-300';
                        case 'Warning':
                            return 'bg-yellow-100 text-yellow-800 border-yellow-300';
                        case 'Info':
                            return 'bg-blue-100 text-blue-800 border-blue-300';
                        default:
                            return 'bg-gray-100 text-gray-800 border-gray-300';
                    }
                };

                return (
                    <Badge
                        variant="outline"
                        className={getAlertTypeColor(row.original.alertType)}
                    >
                        {row.original.alertType}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'duration',
            header: 'Duration (sec)',
            cell: ({ row }) => (
                <div className="text-sm text-gray-600">
                    {row.original.duration}s
                </div>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const getStatusColor = (status: string) => {
                    const statusLower = status.toLowerCase();
                    switch (statusLower) {
                        case 'sent':
                            return 'bg-green-100 text-green-800 border-green-300';
                        case 'ready':
                            return 'bg-yellow-100 text-yellow-800 border-yellow-300';
                        case 'fail':
                        case 'failed':
                            return 'bg-red-100 text-red-800 border-red-300';
                        default:
                            return 'bg-gray-100 text-gray-800 border-gray-300';
                    }
                };

                return (
                    <Badge
                        variant="outline"
                        className={getStatusColor(row.original.status)}
                    >
                        {row.original.status}
                    </Badge>
                );
            },
        },
        ...(canEdit || canDelete ? [{
            id: 'actions',
            header: 'Actions',
            cell: ({ row }: any) => (
                <div className="flex gap-1">
                    <Tooltip title="View Details" arrow>
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 py-0.5 text-sm whitespace-nowrap"
                            onClick={() => navigate(routePaths.alerts.detail(row.original.id.toString()))}
                        >
                            View
                        </Button>
                    </Tooltip>
                    <PermissionGuard module="alerts" action="edit">
                        <Tooltip title="Send Alert" arrow>
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 py-0.5 text-sm whitespace-nowrap"
                                onClick={() => onSendAlert(row.original)}
                            >
                                Send
                            </Button>
                        </Tooltip>
                        <Tooltip title="Edit Alert" arrow>
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 py-0.5 text-sm whitespace-nowrap"
                                onClick={() => onEdit(row.original)}
                            >
                                Edit
                            </Button>
                        </Tooltip>
                    </PermissionGuard>
                    <PermissionGuard module="alerts" action="delete">
                        <Tooltip title="Delete Alert" arrow>
                            <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2 py-0.5 text-sm whitespace-nowrap"
                                onClick={() => onDelete(row.original)}
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    </PermissionGuard>
                </div>
            )
        }] : [])
    ];
};
